import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UnnotifiedLeadBadge } from "@/components/UnnotifiedLeadBadge";
import { 
  Plus, Search, LayoutGrid, List, MoreVertical,
  Mail, Phone, Building2, DollarSign, Sparkles,
  ArrowLeft, Trash2, CheckSquare, Square, Users, GripVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const STATUSES = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700", border: "border-blue-300" },
  { value: "contacted", label: "Contacted", color: "bg-yellow-100 text-yellow-700", border: "border-yellow-300" },
  { value: "qualified", label: "Qualified", color: "bg-purple-100 text-purple-700", border: "border-purple-300" },
  { value: "proposal", label: "Proposal", color: "bg-indigo-100 text-indigo-700", border: "border-indigo-300" },
  { value: "won", label: "Won", color: "bg-green-100 text-green-700", border: "border-green-300" },
  { value: "lost", label: "Lost", color: "bg-red-100 text-red-700", border: "border-red-300" },
];

const getStatusColor = (status) => {
  const found = STATUSES.find(s => s.value === status);
  return found?.color || "bg-slate-100 text-slate-700";
};

const getScoreColor = (score) => {
  if (score >= 70) return "score-high";
  if (score >= 40) return "score-medium";
  return "score-low";
};

export default function LeadsPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("kanban");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLead, setNewLead] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    company: "", source: "manual", value: 0, notes: ""
  });

  // Batch selection
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  // Drag and drop
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        params: { refresh: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      setLeads(response.data);
    } catch (error) {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    const interval = window.setInterval(fetchLeads, 30000);
    window.addEventListener("focus", fetchLeads);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", fetchLeads);
    };
  }, [fetchLeads]);

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/leads`, newLead);
      setLeads([response.data, ...leads]);
      setShowAddModal(false);
      setNewLead({ first_name: "", last_name: "", email: "", phone: "", company: "", source: "manual", value: 0, notes: "" });
      toast.success("Lead added successfully");
    } catch (error) {
      toast.error("Failed to add lead");
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus });
      setLeads(leads.map(l => l.lead_id === leadId ? { ...l, status: newStatus } : l));
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      const res = await axios.delete(`${API}/leads/${leadId}`);
      if (res.data?.detail?.includes("approval")) {
        toast.info("Deletion request sent to owner for approval");
      } else {
        setLeads(leads.filter(l => l.lead_id !== leadId));
        toast.success("Lead moved to trash");
      }
      selectedLeads.delete(leadId);
      setSelectedLeads(new Set(selectedLeads));
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to delete lead";
      if (msg.includes("approval")) toast.info(msg);
      else toast.error(msg);
    }
  };

  const handleAIScore = async (leadId) => {
    try {
      toast.loading("AI is scoring the lead...");
      const response = await axios.post(`${API}/ai/score-lead`, { lead_id: leadId });
      setLeads(leads.map(l => l.lead_id === leadId ? { ...l, score: response.data.score } : l));
      toast.dismiss();
      toast.success(`Lead scored: ${response.data.score}/100`);
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to score lead");
    }
  };

  // Batch actions
  const handleBatchAction = async (action, status) => {
    const ids = Array.from(selectedLeads);
    if (ids.length === 0) return;

    if (action === "delete" && !window.confirm(`Delete ${ids.length} lead(s)?`)) return;

    try {
      await axios.post(`${API}/leads/batch-update`, {
        lead_ids: ids,
        action,
        status
      });
      if (action === "delete") {
        setLeads(leads.filter(l => !selectedLeads.has(l.lead_id)));
        toast.success(`${ids.length} lead(s) deleted`);
      } else {
        setLeads(leads.map(l => selectedLeads.has(l.lead_id) ? { ...l, status } : l));
        toast.success(`${ids.length} lead(s) moved to ${status}`);
      }
      setSelectedLeads(new Set());
      setSelectMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Batch action failed");
    }
  };

  const toggleSelect = (leadId) => {
    const next = new Set(selectedLeads);
    if (next.has(leadId)) next.delete(leadId);
    else next.add(leadId);
    setSelectedLeads(next);
  };

  const selectAll = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(l => l.lead_id)));
    }
  };

  // Drag and drop handlers
  const handleDragStart = useCallback((e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", lead.lead_id);
    e.currentTarget.style.opacity = "0.5";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedLead(null);
    setDragOverColumn(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragOver = useCallback((e, statusValue) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(statusValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverColumn(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrop = useCallback((e, targetStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (draggedLead && draggedLead.status !== targetStatus) {
      handleStatusChange(draggedLead.lead_id, targetStatus);
    }
    setDraggedLead(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggedLead]);

  const filteredLeads = leads.filter(lead => {
    const s = searchTerm.toLowerCase();
    return (
      lead.first_name?.toLowerCase().includes(s) ||
      lead.last_name?.toLowerCase().includes(s) ||
      lead.email?.toLowerCase().includes(s) ||
      lead.company?.toLowerCase().includes(s)
    );
  });

  const leadsByStatus = STATUSES.reduce((acc, status) => {
    acc[status.value] = filteredLeads.filter(l => l.status === status.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Leads</h1>
          <p className="text-slate-600 mt-1">{leads.length} total leads</p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
          <div className="relative min-w-0 flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input type="text" placeholder="Search leads..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 sm:w-64" data-testid="lead-search" />
          </div>
          <div className="flex items-center border border-slate-200 rounded-lg p-1">
            <Button variant={viewMode === "kanban" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("kanban")} data-testid="view-kanban">
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button variant={viewMode === "list" ? "secondary" : "ghost"} size="sm" onClick={() => setViewMode("list")} data-testid="view-list">
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Button variant={selectMode ? "secondary" : "outline"} size="sm" onClick={() => { setSelectMode(!selectMode); setSelectedLeads(new Set()); }} data-testid="select-mode-btn">
            <CheckSquare className="w-4 h-4 mr-1" />
            Select
          </Button>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Lead
          </Button>
        </div>
      </div>

      {/* Batch Action Bar */}
      {selectMode && selectedLeads.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="batch-bar">
          <Button variant="ghost" size="sm" onClick={selectAll} data-testid="select-all-btn">
            {selectedLeads.size === filteredLeads.length ? <CheckSquare className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
            {selectedLeads.size === filteredLeads.length ? "Deselect All" : "Select All"}
          </Button>
          <span className="text-sm font-medium text-indigo-700">{selectedLeads.size} selected</span>
          <div className="h-4 w-px bg-indigo-300" />
          {STATUSES.map(s => (
            <Button key={s.value} variant="ghost" size="sm" className={`text-xs ${s.color}`} onClick={() => handleBatchAction("status", s.value)} data-testid={`batch-${s.value}`}>
              {s.label}
            </Button>
          ))}
          <div className="h-4 w-px bg-indigo-300" />
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleBatchAction("delete")} data-testid="batch-delete">
            <Trash2 className="w-4 h-4 mr-1" /> Delete
          </Button>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <div className="kanban-board flex gap-6 overflow-x-auto pb-4" data-testid="kanban-board">
          {STATUSES.map((status) => (
            <div
              key={status.value}
              className={`kanban-col kanban-column transition-colors ${dragOverColumn === status.value ? `ring-2 ring-offset-2 ring-indigo-400 ${status.border} bg-indigo-50/30` : ""}`}
              onDragOver={(e) => handleDragOver(e, status.value)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, status.value)}
              data-testid={`kanban-col-${status.value}`}
            >
              <div className="kanban-col-header">
                <div className="flex items-center gap-2">
                  <Badge className={status.color}>{status.label}</Badge>
                  <span className="text-sm text-slate-500">{leadsByStatus[status.value]?.length || 0}</span>
                </div>
              </div>
              <div className="space-y-3">
                {leadsByStatus[status.value]?.map((lead) => (
                  <LeadCard
                    key={lead.lead_id}
                    lead={lead}
                    onStatusChange={handleStatusChange}
                    onDelete={handleDeleteLead}
                    onAIScore={handleAIScore}
                    onClick={() => { if (!selectMode) navigate(`/leads/${lead.lead_id}`); else toggleSelect(lead.lead_id); }}
                    selectMode={selectMode}
                    selected={selectedLeads.has(lead.lead_id)}
                    onToggleSelect={() => toggleSelect(lead.lead_id)}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedLead?.lead_id === lead.lead_id}
                  />
                ))}
                {leadsByStatus[status.value]?.length === 0 && (
                  <div className={`text-center py-8 text-slate-400 text-sm border-2 border-dashed rounded-lg ${dragOverColumn === status.value ? "border-indigo-300 bg-indigo-50" : "border-transparent"}`}>
                    {dragOverColumn === status.value ? "Drop here" : "No leads"}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <Card data-testid="leads-table">
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  {selectMode && (
                    <th className="w-10">
                      <button onClick={selectAll} className="p-1">
                        {selectedLeads.size === filteredLeads.length ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                      </button>
                    </th>
                  )}
                  <th>Name</th><th>Email</th><th>Company</th><th>Status</th><th>Score</th><th>Value</th><th>Source</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.lead_id} className={`cursor-pointer ${selectedLeads.has(lead.lead_id) ? "bg-indigo-50" : ""}`}
                    onClick={() => { if (!selectMode) navigate(`/leads/${lead.lead_id}`); else toggleSelect(lead.lead_id); }}
                  >
                    {selectMode && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => toggleSelect(lead.lead_id)} className="p-1">
                          {selectedLeads.has(lead.lead_id) ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                        </button>
                      </td>
                    )}
                    <td className="font-medium">
                      <div className="flex flex-col items-start gap-1">
                        <span>{lead.first_name} {lead.last_name}</span>
                        <UnnotifiedLeadBadge status={lead.admin_notification_status} recordId={lead.lead_id} context="lead-list" />
                      </div>
                    </td>
                    <td className="text-slate-600">{lead.email}</td>
                    <td className="text-slate-600">{lead.company || "-"}</td>
                    <td><Badge className={getStatusColor(lead.status)}>{lead.status}</Badge></td>
                    <td><Badge className={getScoreColor(lead.score)}>{lead.score || 0}</Badge></td>
                    <td className="text-slate-600">${(lead.value || 0).toLocaleString()}</td>
                    <td className="text-slate-500 capitalize">{lead.source}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon"><MoreVertical className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleAIScore(lead.lead_id)}>
                            <Sparkles className="w-4 h-4 mr-2" /> AI Score
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteLead(lead.lead_id)} className="text-red-600">Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredLeads.length === 0 && (
              <div className="empty-state py-12">
                <div className="empty-state-icon"><Users className="w-8 h-8 text-slate-400" /></div>
                <p className="text-slate-600 font-medium">No leads found</p>
                <p className="text-sm text-slate-400">Add your first lead to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Lead Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto max-w-md">
          <DialogHeader><DialogTitle>Add New Lead</DialogTitle></DialogHeader>
          <form onSubmit={handleAddLead} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="form-field">
                <Label>First Name *</Label>
                <Input value={newLead.first_name} onChange={(e) => setNewLead({ ...newLead, first_name: e.target.value })} required data-testid="lead-firstname" />
              </div>
              <div className="form-field">
                <Label>Last Name *</Label>
                <Input value={newLead.last_name} onChange={(e) => setNewLead({ ...newLead, last_name: e.target.value })} required data-testid="lead-lastname" />
              </div>
            </div>
            <div className="form-field">
              <Label>Email *</Label>
              <Input type="email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} required data-testid="lead-email" />
            </div>
            <div className="form-field">
              <Label>Phone</Label>
              <Input value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} data-testid="lead-phone" />
            </div>
            <div className="form-field">
              <Label>Company</Label>
              <Input value={newLead.company} onChange={(e) => setNewLead({ ...newLead, company: e.target.value })} data-testid="lead-company" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="form-field">
                <Label>Source</Label>
                <Select value={newLead.source} onValueChange={(v) => setNewLead({ ...newLead, source: v })}>
                  <SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="google_ads">Google Ads</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="referral">Referral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-field">
                <Label>Value ($)</Label>
                <Input type="number" value={newLead.value} onChange={(e) => setNewLead({ ...newLead, value: parseFloat(e.target.value) || 0 })} data-testid="lead-value" />
              </div>
            </div>
            <div className="form-field">
              <Label>Notes</Label>
              <Textarea value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })} rows={3} data-testid="lead-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-lead-btn">Add Lead</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Lead Card Component
function LeadCard({ lead, onStatusChange, onDelete, onAIScore, onClick, selectMode, selected, onToggleSelect, onDragStart, onDragEnd, isDragging }) {
  return (
    <div
      className={`lead-card animate-fade-in relative transition-all ${isDragging ? "opacity-50 scale-95" : ""} ${selected ? "ring-2 ring-indigo-400 bg-indigo-50/50" : ""}`}
      onClick={onClick}
      draggable={!selectMode}
      onDragStart={(e) => onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      data-testid={`lead-card-${lead.lead_id}`}
      style={{ cursor: selectMode ? "pointer" : "grab" }}
    >
      {/* Select checkbox */}
      {selectMode && (
        <button
          className="absolute top-2 left-2 z-10"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          data-testid={`select-${lead.lead_id}`}
        >
          {selected ? <CheckSquare className="w-5 h-5 text-indigo-600" /> : <Square className="w-5 h-5 text-slate-400" />}
        </button>
      )}

      {/* Drag handle */}
      {!selectMode && (
        <div className="absolute top-2 left-1 text-slate-300 cursor-grab">
          <GripVertical className="w-4 h-4" />
        </div>
      )}

      <div className={`${selectMode ? "pl-7" : "pl-5"}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-medium text-slate-900">{lead.first_name} {lead.last_name}</p>
            <div className="mt-1">
              <UnnotifiedLeadBadge status={lead.admin_notification_status} recordId={lead.lead_id} context="lead-card" />
            </div>
            {lead.company && (
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                <Building2 className="w-3 h-3" /> {lead.company}
              </p>
            )}
          </div>
          {!selectMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {STATUSES.map((status) => (
                  <DropdownMenuItem key={status.value} onClick={(e) => { e.stopPropagation(); onStatusChange(lead.lead_id, status.value); }}>
                    <span className={`w-2 h-2 rounded-full mr-2 ${status.color.split(" ")[0]}`} />
                    Move to {status.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onAIScore(lead.lead_id); }}>
                  <Sparkles className="w-4 h-4 mr-2" /> AI Score
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDelete(lead.lead_id); }} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="space-y-2 text-sm">
          <p className="text-slate-600 flex items-center gap-2"><Mail className="w-3 h-3" /> {lead.email}</p>
          {lead.phone && <p className="text-slate-600 flex items-center gap-2"><Phone className="w-3 h-3" /> {lead.phone}</p>}
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            {lead.score > 0 && <Badge className={getScoreColor(lead.score)}>Score: {lead.score}</Badge>}
          </div>
          {lead.value > 0 && (
            <p className="text-sm font-medium text-green-600 flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> {lead.value.toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
